import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { Typography } from '@material-ui/core';
import Config from 'config';
import { sortAlphabeticallyByKeys } from 'utilities/sort-util';
import useReportQueryParams from '../../../../hooks/use-report-query-params';
import { ReportView } from '../../../../types';
import { resolveDateRange } from '../../../../utils/report-date-utils';
import EngagementChartView from './EngagementChartView';
import EngagementTableView from './EngagementTableView';
import { gql } from 'types/__generated__/gql';

const KEY_ORGANIZATION_ENGAGEMENT_ASYNC_QUERY = gql(/* GraphQL */ `
  query keyOrganizationEngagementReportAsync(
    $where: KeyOrganizationEngagementReportWhereInput!
    $dateRange: DateRangeInput!
  ) {
    keyOrganizationEngagementReportAsync(where: $where, dateRange: $dateRange) {
      id
      error
      result {
        ...OrganizationEngagement
      }
    }
  }
`);

const ORGANIZATION_NAME_SORT = (a: any, b: any): number =>
  sortAlphabeticallyByKeys(['organization.name'], a, b);

const SchoolEngagementCards: React.FC = () => {
  const [{ entityId, entityType, dateRange, viewAs }] = useReportQueryParams();
  const { t } = useTranslation('Reports');
  const dateRangeObject = useMemo(() => resolveDateRange(dateRange), [dateRange]);
  const [fetchReport, { data, error, stopPolling }] = useLazyQuery(
    KEY_ORGANIZATION_ENGAGEMENT_ASYNC_QUERY,
    {
      pollInterval: Config.reporting.asyncPollIntervalMs
    }
  );

  useEffect(() => {
    fetchReport({
      variables: { where: { organizationId: entityId as number }, dateRange: dateRangeObject }
    });
  }, [entityType, entityId, fetchReport, dateRangeObject]);

  let reportData: any = [];

  if (error) {
    stopPolling?.();
  } else if (data?.keyOrganizationEngagementReportAsync?.result) {
    stopPolling?.();
    reportData = data.keyOrganizationEngagementReportAsync.result
      .slice()
      .sort(ORGANIZATION_NAME_SORT);
  }

  const REPORT_VIEW_MAP = {
    [ReportView.CHART]: (
      <EngagementChartView
        reportData={reportData}
        loading={!data?.keyOrganizationEngagementReportAsync?.result}
      />
    ),
    [ReportView.TABLE]: (
      <EngagementTableView
        reportData={reportData}
        loading={!data?.keyOrganizationEngagementReportAsync?.result}
      />
    )
  };

  return (
    <section>
      <Typography variant="h3">{t('Engagement')}</Typography>
      {REPORT_VIEW_MAP[viewAs]}
    </section>
  );
};

export default SchoolEngagementCards;
